
/**
 * Module dependencies.
 */

import { RecoverPasswordFormData as FormData } from 'src/types/user';
import { GetStaticProps, NextPage } from 'next';
import { SSRProps } from 'src/types/app';
import { Type } from 'src/components/core/typography';
import { getNetworkErrorTranslatedKey } from 'src/core/utils/errors';
import { media, units } from '@untile/react-components/dist/styles';
import { recoverPassword } from 'src/api/password/recover-password';
import { regexes } from 'src/core/utils/regexes';
import { routes } from 'src/core/routes';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslation } from 'next-i18next';
import AuthenticationLayout from 'src/components/layout/authentication';
import Button from 'src/components/core/buttons/button';
import Input from 'src/components/core/forms/fields/input';
import Metatags from 'src/components/core/metatags';
import styled from 'styled-components';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  padding: ${units(8)} 0 ${units(5)};

  ${media.min('ms')`
    margin: 0 auto;
    width: 50%;
  `}

  ${media.min('md')`
    margin: 0;
    width: calc(6 * 100% / 8.5);
  `}
`;

/**
 * `BackButton` styled component.
 */

const BackButton = styled(Button).attrs({ colorTheme: 'tertiary' })`
  margin-bottom: ${units(5)};

  ${media.min('md')`
    margin-bottom: ${units(17)};
    margin-left: ${units(2)};
  `}
`;

/**
 * `RecoverPassword` page.
 */

const RecoverPassword: NextPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { showMessage } = useSnackbar();
  const {
    formState,
    handleSubmit,
    register
  } = useForm<FormData>({ mode: 'onBlur' });

  const { isLoading, mutate } = useMutation(recoverPassword);
  const errors = formState?.errors;
  const isSubmitting = isLoading || formState?.isSubmitting;
  const handleRecoverPassword = useCallback((formData: FormData) => {
    return mutate({ formData }, {
      onError: ({ data }) => {
        showMessage(
          t(getNetworkErrorTranslatedKey({
            basePath: 'recover-password:',
            errorCode: data?.code,
            name: 'recoverPassword'
          })),
          { appearance: 'error' }
        );
      },
      onSuccess: () => {
        router.push(routes.recoverPasswordSuccess);
      }
    });
  }, [mutate, router, showMessage, t]);

  return (
    <AuthenticationLayout>
      <Metatags title={t('recover-password:metatags.title')} />

      <Wrapper>
        <BackButton href={routes.signIn}>
          {t('common:actions.backToSignIn')}
        </BackButton>

        <Type.H1
          marginBottom={units(3)}
          marginBottomMd={units(5)}
        >
          {t('recover-password:title')}
        </Type.H1>

        <form onSubmit={handleSubmit(handleRecoverPassword)}>
          <Input
            disabled={isSubmitting}
            error={errors?.email}
            isRequired
            label={t('common:forms.labels.email')}
            name={'email'}
            placeholder={t('common:forms.placeholders.email')}
            {...register('email', {
              pattern: {
                message: t('common:forms.rules.emailFormat'),
                value: regexes.email
              },
              required: {
                message: t('common:forms.rules.required'),
                value: true
              }
            })}
            type={'email'}
          />

          <Button
            isLoading={isSubmitting}
            type={'submit'}
          >
            {t('recover-password:form.submitButton')}
          </Button>
        </form>
      </Wrapper>
    </AuthenticationLayout>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSRProps> = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, ['common', 'recover-password']),
    layout: 'authentication'
  },
  revalidate: 60
});

/**
 * Export `RecoverPassword` page.
 */

export default RecoverPassword;
